import React, { useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

type SelectOption = {
  key: React.Key;
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

type SelectPanelPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
};

export interface SelectProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'defaultValue' | 'name' | 'onBlur' | 'onChange' | 'onKeyDown' | 'type' | 'value'
  > {
  children: React.ReactNode;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  name?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
  panelClassName?: string;
  containerClassName?: string;
  menuMaxHeight?: number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onValueChange?: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

const textFromNode = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textFromNode).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return textFromNode(node.props.children);
  }
  return '';
};

const optionValue = (value: unknown, children: React.ReactNode) => {
  if (value === undefined || value === null) return textFromNode(children);
  return String(value);
};

const flattenOptions = (children: React.ReactNode): SelectOption[] => {
  const result: SelectOption[] = [];

  const visit = (nodes: React.ReactNode) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;

      if (child.type === React.Fragment) {
        visit((child.props as { children?: React.ReactNode }).children);
        return;
      }

      if (child.type === 'optgroup') {
        visit((child.props as { children?: React.ReactNode }).children);
        return;
      }

      if (child.type !== 'option') return;

      const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
      const value = optionValue(props.value, props.children);
      result.push({
        key: child.key ?? `${value}-${result.length}`,
        value,
        label: props.children ?? value,
        disabled: props.disabled,
      });
    });
  };

  visit(children);
  return result;
};

const makeChangeEvent = (value: string, name?: string) =>
  ({
    target: { value, name },
    currentTarget: { value, name },
  }) as unknown as React.ChangeEvent<HTMLSelectElement>;

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      name,
      value,
      defaultValue,
      children,
      placeholder = 'Выберите значение',
      className,
      panelClassName,
      containerClassName,
      menuMaxHeight = 288,
      disabled,
      required,
      autoFocus,
      onChange,
      onValueChange,
      onKeyDown,
      onBlur,
      ...props
    },
    forwardedRef,
  ) => {
    const reactId = useId();
    const selectId = id ?? `select-${reactId}`;
    const listboxId = `${selectId}-listbox`;
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
    const [position, setPosition] = useState<SelectPanelPosition | null>(null);
    const options = useMemo(() => flattenOptions(children), [children]);
    const enabledOptions = useMemo(() => options.filter((option) => !option.disabled), [options]);
    const firstEnabledValue = enabledOptions[0]?.value ?? '';
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(() =>
      defaultValue !== undefined ? String(defaultValue) : firstEnabledValue,
    );
    const currentValue = isControlled ? String(value ?? '') : internalValue;
    const selectedOption = options.find((option) => option.value === currentValue);
    const selectedLabel = selectedOption?.label ?? (currentValue ? currentValue : placeholder);
    const classText = typeof className === 'string' ? className : '';
    const fillContainer = /\b(w-full|block|flex-1)\b/.test(classText);

    useImperativeHandle(forwardedRef, () => triggerRef.current as unknown as HTMLSelectElement);

    useEffect(() => {
      if (!autoFocus) return;
      triggerRef.current?.focus();
    }, [autoFocus]);

    useEffect(() => {
      if (isControlled || currentValue || !firstEnabledValue) return;
      setInternalValue(firstEnabledValue);
    }, [currentValue, firstEnabledValue, isControlled]);

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(Math.max(rect.width, 220), viewportWidth - 16);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - width - 8));
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const placeAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      const available = Math.max(96, placeAbove ? spaceAbove - 4 : spaceBelow - 4);
      const next: SelectPanelPosition = {
        left,
        width,
        maxHeight: Math.min(menuMaxHeight, available),
        ...(placeAbove ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      };

      setPosition(next);
    };

    useEffect(() => {
      if (!open) return;

      updatePosition();
      const onScrollOrResize = () => updatePosition();
      window.addEventListener('resize', onScrollOrResize);
      window.addEventListener('scroll', onScrollOrResize, true);

      return () => {
        window.removeEventListener('resize', onScrollOrResize);
        window.removeEventListener('scroll', onScrollOrResize, true);
      };
    }, [open, menuMaxHeight]);

    useEffect(() => {
      if (!open) return;

      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as Node;
        if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
        setOpen(false);
      };

      document.addEventListener('mousedown', onPointerDown);
      return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
      if (!open) return;
      setHighlightedValue(selectedOption && !selectedOption.disabled ? selectedOption.value : firstEnabledValue || null);
    }, [firstEnabledValue, open, selectedOption]);

    const emitChange = (nextValue: string) => {
      if (!isControlled) setInternalValue(nextValue);
      onValueChange?.(nextValue);
      onChange?.(makeChangeEvent(nextValue, name));
    };

    const chooseValue = (nextValue: string) => {
      const option = options.find((item) => item.value === nextValue);
      if (!option || option.disabled) return;
      emitChange(nextValue);
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
    };

    const moveHighlight = (direction: 1 | -1) => {
      if (enabledOptions.length === 0) return;
      const currentIndex = Math.max(
        0,
        enabledOptions.findIndex((option) => option.value === (highlightedValue ?? currentValue)),
      );
      const nextIndex = (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
      const next = enabledOptions[nextIndex];
      setHighlightedValue(next.value);
      const optionIndex = options.findIndex((option) => option.value === next.value);
      document.getElementById(`${listboxId}-option-${optionIndex}`)?.scrollIntoView({ block: 'nearest' });
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) setOpen(true);
        else moveHighlight(-1);
      } else if (event.key === 'Home' && open && enabledOptions.length > 0) {
        event.preventDefault();
        setHighlightedValue(enabledOptions[0].value);
      } else if (event.key === 'End' && open && enabledOptions.length > 0) {
        event.preventDefault();
        setHighlightedValue(enabledOptions[enabledOptions.length - 1].value);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (highlightedValue) {
          chooseValue(highlightedValue);
        }
      } else if (event.key === 'Escape') {
        // Закрытый список Esc не трогает: его получит окно вокруг. Открытый — закрывается и гасит событие.
        if (!open) return;
        event.preventDefault();
        setOpen(false);
      }
    };

    const handleBlur: React.FocusEventHandler<HTMLButtonElement> = (event) => {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active && (triggerRef.current?.contains(active) || panelRef.current?.contains(active))) return;
        onBlur?.(event);
      }, 0);
    };

    const panel = open && position ? (
      <div
        ref={panelRef}
        id={listboxId}
        role="listbox"
        aria-labelledby={selectId}
        className={cn(
          'fixed z-[70] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg',
          'dark:border-zinc-800 dark:bg-zinc-900',
          panelClassName,
        )}
        style={{
          left: position.left,
          top: position.top,
          bottom: position.bottom,
          width: position.width,
          maxHeight: position.maxHeight,
        }}
      >
        {options.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-400 dark:text-zinc-500">Нет вариантов</div>
        ) : (
          options.map((option, optionIndex) => {
            const selected = option.value === currentValue;
            const highlighted = option.value === highlightedValue;

            return (
              <button
                id={`${listboxId}-option-${optionIndex}`}
                key={option.key}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.disabled}
                onMouseEnter={() => !option.disabled && setHighlightedValue(option.value)}
                onClick={() => chooseValue(option.value)}
                className={cn(
                  'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                  selected
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                    : 'text-gray-700 dark:text-zinc-200',
                  highlighted && !selected && 'bg-gray-50 dark:bg-zinc-800/70',
                  option.disabled && 'cursor-not-allowed text-gray-400 dark:text-zinc-500',
                )}
              >
                <span className="min-w-0 flex-1 whitespace-normal break-words">{option.label}</span>
                {selected && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    ) : null;

    return (
      <div
        className={cn(
          'relative max-w-full',
          fillContainer ? 'block w-full' : 'inline-block',
          containerClassName,
        )}
      >
        <button
          {...props}
          ref={triggerRef}
          id={selectId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-required={required}
          disabled={disabled}
          onClick={() => !disabled && setOpen((next) => !next)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={cn(
            'inline-flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
            'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-70',
            'dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-500',
            className,
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate', !selectedOption && 'text-gray-400 dark:text-zinc-500')}>
            {selectedLabel}
          </span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-zinc-500', open && 'rotate-180')}
          />
        </button>
        {name && <input type="hidden" name={name} value={currentValue} required={required} />}
        {panel && createPortal(panel, document.body)}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
